/* eslint-disable react-hooks/exhaustive-deps */
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";

import { QuestionType } from "types/dataVoteType";
import useVoteState, {
    VoteStateGroupedDeliberation,
    Person,
    Answer,
    VoteStatus,
    canGroupedAnswerBeUpdated,
} from "./useVoteState";
import { Socket } from "socket.io-client";
import getAndFormatAnswers from "../services/getAndFormatAnswers";
import emitAsyncAnswer from "components/helper/emitAsyncAnswer";
import { useSession } from "components/helper/sessionContext";
import { CSS_VARIABLES } from "enums/cssVariables";
import { onBackgroundImageSx } from "components/common/backgroundFrame";
import icon from "assets/auto_awesome_motion.png";

import VoteBasic from "../../common/VoteBasic";
import VoteDistribution from "../../common/VoteDistribution";
import ParticipantButton from "./ParticipantButton";
import ActionSection from "../../common/ActionSection";
import ParticipantHeader from "../../common/ParticipantHeader";
import ToggleSwitch from "../../common/AutomaticVoteForProxiesComponents/ToggleSwitch";
import { Box, Divider, Paper } from "@mui/material";
import VoteHeader from "../../common/Header/VoteHeader";

type Props = {
    questions: Array<QuestionType>;
    user: Person;
    proxies: Array<Person>;
    title: string;
    publicKey: string;
};

declare global {
    interface Window {
        socketClient: Socket;
        constant: { events: { browsers: { ANSWER: string } } };
    }
}

type FormattedAnswer = {
    questionId: number;
    answer: string;
    weight: number;
};

const GroupedDeliberation = ({
    questions,
    user,
    proxies,
    title,
    publicKey,
}: Props) => {
    const { socket } = useSession();
    const [votes, dispatchVoteAction] = useVoteState(questions, proxies, user);
    const [
        holderVoteMustBeAppliedToProxies,
        setHolderVoteMustBeAppliedToProxies,
    ] = useState(false);
    // Tracks if holder's votes were already applied to proxies via the toggle to prevent duplicates.
    const [hasAppliedHolderVotesToProxies, setHasAppliedHolderVotesToProxies] =
        useState(false);
    // Signal to effect for send proxy votes after state update.
    const [shouldSendProxyVotes, setShouldSendProxyVotes] = useState(false);
    const [globalSelection, setGlobalSelection] = useState<string | null>(null);

    const userVote = votes.find(
        (vote) => vote.voterId === user.id,
    ) as VoteStateGroupedDeliberation;

    const sendVote = async (voterId: string, answers: FormattedAnswer[]) => {
        dispatchVoteAction({ type: "sendAnswersRequest", voterId });

        try {
            await emitAsyncAnswer(socket, {
                votingDeviceId: voterId,
                publicKey,
                answers,
            });

            dispatchVoteAction({ type: "sendAnswersSuccess", voterId });
        } catch {
            dispatchVoteAction({ type: "sendAnswersError", voterId });
        }
    };

    // Handle toggle switch turn on
    useEffect(() => {
        if (
            !holderVoteMustBeAppliedToProxies ||
            userVote.voteStatus !== VoteStatus.Success
        ) {
            setHasAppliedHolderVotesToProxies(false);
            return;
        }

        // If the votes have already been applied to proxies, don't apply them again
        if (hasAppliedHolderVotesToProxies) return;

        applyVotesToProxies();
        setHasAppliedHolderVotesToProxies(true);
    }, [
        holderVoteMustBeAppliedToProxies,
        userVote.voteStatus,
        hasAppliedHolderVotesToProxies,
    ]);

    // Allow to send proxy votes once their state is updated.
    useEffect(() => {
        if (!shouldSendProxyVotes) return;

        const sendProxyVotes = async () => {
            setShouldSendProxyVotes(false);

            for (const proxy of proxies) {
                const proxyVote = votes.find(
                    (vote) => vote.voterId === proxy.id,
                ) as VoteStateGroupedDeliberation | undefined;

                if (
                    !proxyVote ||
                    proxyVote.voteStatus !== VoteStatus.PendingValidation
                ) {
                    continue;
                }

                const answers = getAndFormatAnswers(proxyVote);

                if (answers.length === 0) {
                    continue;
                }

                await sendVote(proxy.id, answers);
            }
        };

        sendProxyVotes();
    }, [shouldSendProxyVotes, proxies, votes]);

    const { t } = useTranslation();

    const applyVotesToProxies = () => {
        if (!hasAnsweredEveryDeliberation(userVote)) {
            return;
        }

        // Apply holder votes to proxies in reducer
        userVote.deliberations.forEach((deliberation) => {
            deliberation.answers.forEach((answer) => {
                if (answer.weight > 0) {
                    dispatchVoteAction({
                        type: "replaceVoteForEveryParticipant",
                        id: answer.id,
                        idDeliberation: deliberation.idDeliberation,
                    });
                }
            });
        });
        // Reset the signal to send proxy votes
        setShouldSendProxyVotes(true);
    };

    const renderGlobalVote = () => {
        if (questions.some((q) => q.authorizeWeightDistribution)) return null;
        //Build an array with all proposals
        const allPropositions = questions.flatMap((q) => q.propositions);

        // Use a Set to keep track of which IDs we've already included
        const seenIds = new Set<string>();

        // Remove duplicates: only keep the first occurrence of each proposition ID
        const globalProps = allPropositions.filter((prop) => {
            if (seenIds.has(prop.id)) {
                // if we’ve already seen this ID, skip it
                return false;
            }
            // add
            seenIds.add(prop.id);
            return true;
        });

        return (
            <>
                <Paper
                    elevation={0}
                    aria-label="global-deliberation"
                    sx={{
                        mb: 2,
                        padding: "0.5rem",
                        border: `1px solid var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR})`,
                        // Inside the frame, the panel shares the frame background instead of an opaque white one.
                        ...onBackgroundImageSx({
                            backgroundColor: "transparent",
                        }),
                    }}
                >
                    <Box display="flex" alignItems="center" mb={0.5}>
                        <Icon src={icon} />
                        <StyledApplyAllTitle>
                            {t("globalVoteTitle")}
                        </StyledApplyAllTitle>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <VoteBasic
                        propositions={globalProps}
                        answers={globalProps.map((p) => ({
                            id: p.id,
                            weight: p.id === globalSelection ? user.weight : 0,
                            disableOtherPropositionsIfSelected: true,
                        }))}
                        isSecret={questions.every((q) => q.isSecret)}
                        voteStatus={userVote.voteStatus}
                        replaceProposal={(propId: string) => {
                            questions.forEach((q) => {
                                const deliberation =
                                    userVote.deliberations.find(
                                        (d) => d.idDeliberation === q.id,
                                    );
                                const prev = deliberation?.answers.find(
                                    (a) => a.weight > 0,
                                );
                                if (
                                    q.propositions.some((x) => x.id === propId)
                                ) {
                                    dispatchVoteAction({
                                        type: "updateWeightVote",
                                        idDeliberation: q.id,
                                        id: propId,
                                        voterId: user.id,
                                        weight: user.weight,
                                    });
                                } else if (prev) {
                                    dispatchVoteAction({
                                        type: "updateWeightVote",
                                        idDeliberation: q.id,
                                        id: prev.id,
                                        voterId: user.id,
                                        weight: 0,
                                    });
                                }
                            });
                            setGlobalSelection(propId);
                        }}
                        proposalsOrientation="horizontal"
                        voterName={t("globalVoteTitle")}
                    />
                    <InfoMessage>{t("infoGlobalVote")}</InfoMessage>
                </Paper>
                <Divider sx={{ my: 3 }} />
            </>
        );
    };

    const showAutomaticVoteToggleSwitch = () => {
        return (
            user.weight > 0 &&
            questions.every((question) => !question.authorizeWeightDistribution)
        );
    };

    const canAnswerBeUpdated = canGroupedAnswerBeUpdated(questions);

    function hasAnsweredEveryDeliberation(
        vote: VoteStateGroupedDeliberation,
    ): boolean {
        return vote.deliberations.every((deliberation) =>
            deliberation.answers.some((answer) => answer.weight > 0),
        );
    }

    const handleValidate = async (voterId: string) => {
        const voteToSend = votes.find((vote) => vote.voterId === voterId) as
            | VoteStateGroupedDeliberation
            | undefined;

        if (!voteToSend) {
            return;
        }

        if (!hasAnsweredEveryDeliberation(voteToSend)) {
            return;
        }

        const answers = getAndFormatAnswers(voteToSend);

        if (answers.length === 0) {
            return;
        }

        await sendVote(voterId, answers);

        if (voterId === user.id && holderVoteMustBeAppliedToProxies) {
            applyVotesToProxies();
            setHasAppliedHolderVotesToProxies(true);
        }
    };

    const renderVoteBasic = (
        participant: Person,
        participantVote: VoteStateGroupedDeliberation,
        question: QuestionType,
        index: number,
    ) => {
        return (
            <div key={index} aria-label={`deliberation-${question.id}`}>
                <VoteHeader question={question} />
                {participant.weight > 1 ? (
                    <StyledNumberOfVoices>
                        {t("numberOfVote")}
                        {participant.weight}
                    </StyledNumberOfVoices>
                ) : null}
                <VoteBasic
                    propositions={question.propositions}
                    answers={participantVote.deliberations[index].answers}
                    isSecret={question.isSecret}
                    voteStatus={participantVote.voteStatus}
                    replaceProposal={(propositionId: string) => {
                        dispatchVoteAction({
                            type: "updateWeightVote",
                            idDeliberation: question.id,
                            id: propositionId,
                            voterId: participant.id,
                            weight: participant.weight,
                        });
                        setGlobalSelection(null);
                    }}
                    proposalsOrientation="horizontal"
                    voterName={participant.displayName}
                />
            </div>
        );
    };

    const renderVoteDistribution = (
        participant: Person,
        participantVote: VoteStateGroupedDeliberation,
        question: QuestionType,
        index: number,
    ) => {
        return (
            <div key={index} aria-label={`deliberation-${question.id}`}>
                <VoteHeader question={question} />
                <ParticipantHeader
                    participant={participant}
                    participantVote={{
                        answers: participantVote.deliberations[index]
                            .answers as Answer[],
                        voteStatus: participantVote.voteStatus.toString(),
                    }}
                    voteIsWithDistribution={true}
                    isGroupedDeliberation={questions.length > 1}
                />
                <VoteDistribution
                    propositions={question.propositions}
                    answers={participantVote.deliberations[index].answers}
                    isSecret={question.isSecret}
                    voterWeight={participant.weight}
                    voteStatus={participantVote.voteStatus}
                    updateWeight={(propositionId: string, weight: number) => {
                        dispatchVoteAction({
                            type: "updateWeightVote",
                            idDeliberation: question.id,
                            id: propositionId,
                            voterId: participant.id,
                            weight,
                        });
                    }}
                    proposalsOrientation="horizontal"
                />
            </div>
        );
    };

    const renderDeliberations = (
        person: Person,
        stateVote: VoteStateGroupedDeliberation,
    ) => {
        const isOwner = person.id === user.id;
        const canShowGlobalDeliberation =
            isOwner && !questions.some((q) => q.authorizeWeightDistribution);

        return (
            <>
                {canShowGlobalDeliberation && renderGlobalVote()}
                {questions.map((question, index) => {
                    if (
                        question.authorizeWeightDistribution &&
                        1 !== person.weight
                    ) {
                        return renderVoteDistribution(
                            person,
                            stateVote,
                            question,
                            index,
                        );
                    }
                    // No proxies + no weight distribution or plurinominal
                    return renderVoteBasic(person, stateVote, question, index);
                })}

                <ActionSection
                    validateVote={() => handleValidate(person.id)}
                    resetVote={() => {
                        dispatchVoteAction({
                            type: "resetVote",
                            voterId: stateVote.voterId,
                        });
                        setGlobalSelection(null);
                    }}
                    voteStatus={stateVote.voteStatus}
                    canAnswerBeUpdated={canAnswerBeUpdated}
                />
            </>
        );
    };

    const renderVote = () => {
        // Without proxies
        if (0 === proxies.length) {
            return <>{renderDeliberations(user, userVote)}</>;
        }

        //With proxies
        return (
            <>
                <ParticipantButton
                    voteStatus={userVote.voteStatus}
                    voterWeight={user.weight}
                    displayName={user.displayName}
                >
                    {renderDeliberations(user, userVote)}
                </ParticipantButton>
                {showAutomaticVoteToggleSwitch() && (
                    <ToggleSwitch
                        checked={holderVoteMustBeAppliedToProxies}
                        onChange={setHolderVoteMustBeAppliedToProxies}
                    />
                )}
                <p>{t("proxy")}</p>
                {proxies.map((proxy, index) => {
                    const proxyVote = votes.find(
                        (vote) => vote.voterId === proxy.id,
                    ) as VoteStateGroupedDeliberation;

                    return (
                        <ParticipantButton
                            voteStatus={proxyVote.voteStatus}
                            voterWeight={proxy.weight}
                            displayName={proxy.displayName}
                            key={index}
                        >
                            {renderDeliberations(proxy, proxyVote)}
                        </ParticipantButton>
                    );
                })}
            </>
        );
    };

    return (
        <>
            <Wrapper>
                <WrapperVote>
                    <StyledGlobalTitle>{title}</StyledGlobalTitle>
                    {renderVote()}
                </WrapperVote>
            </Wrapper>
        </>
    );
};

const Wrapper = styled.div`
    width: 100%;
`;

const WrapperVote = styled.div`
    margin: auto;
    @media (max-width: 768px) {
        max-width: 350px;
    }
`;

const StyledGlobalTitle = styled.div`
    margin-bottom: 1.5rem;
    font-size: 14px;
`;

const StyledApplyAllTitle = styled.div`
    font-size: 1rem;
    font-weight: bold;
    color: var(${CSS_VARIABLES.PRIMARY_BUTTON_BACKGROUND_COLOR});
`;
const Icon = styled.img`
    width: 20px;
    height: 20px;
    margin-right: 0.5rem;
`;
const StyledNumberOfVoices = styled.div`
    color: #626262;
    padding-bottom: 0.5rem;
    font-size: 14px;
`;
const InfoMessage = styled.p`
    color: #626262;
    font-size: 14px;
    font-style: italic;
`;

export default GroupedDeliberation;
